import React, { useMemo, useState, useEffect, useRef } from "react";
import HeartLoader from "@/components/HeartLoader";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ArrowRight, Loader2, X, ShieldCheck, Layers, AlertTriangle, Clock, CheckCircle2, FileText, MonitorSmartphone, Megaphone, Briefcase, Download, Mail, Share2, MessageCircle, Wand2, Copy, Sparkles, Paperclip, Plus, Bookmark, BarChart3, LayoutGrid, Radar, Rocket, Signal, Globe2, MapPin } from "lucide-react";
import { useApp } from "@/context/AppContext";
import Icon from "@/components/Icon";
import { api, apiError, rememberRedirect } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import TrendingStrip from "@/components/TrendingStrip";

const SOLUTIONS = [
  { key: "Blueprint", icon: FileText, label: "Generate Blueprint", desc: "Architecture & regulatory spec." },
  { key: "Software", icon: MonitorSmartphone, label: "Build Experiential Software", desc: "A deployable, experiential app." },
  { key: "Marketing", icon: Megaphone, label: "Create", desc: "Logo, PPT, PDF, flyer, brochure, article." },
  { key: "Management Consulting", icon: Briefcase, label: "Get Consulting Plan", desc: "Advisory & engagement roadmap." },
  { key: "Insights & Signals", icon: BarChart3, label: "Generate Insights & Signals", desc: "Data-driven insights + live market, tech & regulatory signals." },
  { key: "SWOT Report", icon: LayoutGrid, label: "Build SWOT Report", desc: "Strengths, weaknesses, opportunities, threats." },
  { key: "Competitive Intelligence", icon: Radar, label: "Run Competitive Intel", desc: "Market & competitor deep-dive." },
];
const MARKETING_ASSETS = ["Logo", "PPT", "PDF", "Flyer", "Brochure", "Article"];

// Official-source lookup for compliance standards; falls back to a Google search.
const COMPLIANCE_LINKS = [
  [/hipaa|hitech/i, "https://www.hhs.gov/hipaa/index.html"],
  [/gdpr/i, "https://gdpr.eu/"],
  [/ccpa|cpra/i, "https://oag.ca.gov/privacy/ccpa"],
  [/pci\s*dss/i, "https://www.pcisecuritystandards.org/"],
  [/soc\s*2/i, "https://www.aicpa-cima.com/topic/audit-assurance/audit-and-assurance-greater-than-soc-2"],
  [/iso\/?\s*iec\s*27001|iso\s*27001/i, "https://www.iso.org/standard/27001"],
  [/iso\s*13485/i, "https://www.iso.org/standard/59752.html"],
  [/iso\s*14001/i, "https://www.iso.org/iso-14001-environmental-management.html"],
  [/iso\s*9001/i, "https://www.iso.org/iso-9001-quality-management.html"],
  [/iso\s*50001/i, "https://www.iso.org/iso-50001-energy-management.html"],
  [/iso\s*28000/i, "https://www.iso.org/standard/79612.html"],
  [/fda\s*21\s*cfr\s*part\s*11/i, "https://www.fda.gov/regulatory-information/search-fda-guidance-documents/part-11-electronic-records-electronic-signatures-scope-and-application"],
  [/gxp|gmp|glp|gcp/i, "https://www.fda.gov/drugs/pharmaceutical-quality-resources/facts-about-current-good-manufacturing-practices-cgmp"],
  [/nist\s*csf/i, "https://www.nist.gov/cyberframework"],
  [/nist\s*800-53/i, "https://csrc.nist.gov/pubs/sp/800/53/r5/upd1/final"],
  [/iec\s*62443/i, "https://www.isa.org/standards-and-publications/isa-standards/isa-iec-62443-series-of-standards"],
  [/nerc\s*cip/i, "https://www.nerc.com/pa/Stand/Pages/CIPStandards.aspx"],
  [/fedramp/i, "https://www.fedramp.gov/"],
  [/eidas/i, "https://digital-strategy.ec.europa.eu/en/policies/eidas-regulation"],
  [/wcag/i, "https://www.w3.org/WAI/standards-guidelines/wcag/"],
  [/ferpa/i, "https://studentprivacy.ed.gov/ferpa"],
  [/coppa/i, "https://www.ftc.gov/legal-library/browse/rules/childrens-online-privacy-protection-rule-coppa"],
  [/dmca|copyright/i, "https://www.copyright.gov/dmca/"],
  [/osha/i, "https://www.osha.gov/laws-regs"],
  [/kyc|aml|cft/i, "https://www.fatf-gafi.org/en/topics/fatf-recommendations.html"],
  [/fssai/i, "https://www.fssai.gov.in/"],
  [/ayush/i, "https://ayush.gov.in/"],
  [/fcra/i, "https://fcraonline.nic.in/"],
  [/hl7|fhir/i, "https://www.hl7.org/fhir/"],
  [/c-tpat|aeo/i, "https://www.cbp.gov/border-security/ports-entry/cargo-security/ctpat"],
];
const complianceUrl = (name) => {
  const s = String(name || "");
  const hit = COMPLIANCE_LINKS.find(([re]) => re.test(s));
  return hit ? hit[1] : `https://www.google.com/search?q=${encodeURIComponent(s + " compliance standard")}`;
};

const ORG_SIZES = [
  { key: "personal", label: "Individual / Solo", desc: "Just me" },
  { key: "small", label: "Small Org", desc: "Startup / small team" },
  { key: "mid", label: "Mid-size Org", desc: "Scaling company" },
  { key: "enterprise", label: "Enterprise", desc: "Large / global" },
];
const ORG_SIZE_DESC = {
  personal: "an individual / solo operator (lean, prototype-level, minimal budget)",
  small: "a small organization or startup (MVP to early-scale, lean team)",
  mid: "a mid-size organization (scaling operations, structured processes)",
  enterprise: "a large enterprise (global scale, complex compliance, many stakeholders)",
};
const SUGGESTED_PROJECTS = {
  personal: ["Solo MVP Prototype", "Personal Brand Kit", "Lean Launch Plan"],
  small: ["Prototype Startup", "Go-to-Market Plan", "Seed Pitch Deck"],
  mid: ["Scale-up Playbook", "Series-A Compliance Pack", "Ops Automation Blueprint"],
  enterprise: ["Global Compliance Audit", "Enterprise Architecture Blueprint", "M&A Due-Diligence Report"],
};

export default function Engine({ embedded = false }) {
  const { t, meta, user, setUser, country, setCountry, countryObj, theme } = useApp();
  const nav = useNavigate();
  // Every page is viewable, but any ACTIVITY requires sign-in.
  const gate = () => {
    if (!user) { rememberRedirect(); toast.error("Please sign in to continue."); nav("/auth"); return false; }
    return true;
  };
  const blackBg = !!theme && theme !== "mono" && !theme.endsWith("_light");
  const brand = user?.branding || {};
  const brandFields = () => ({
    brand_company: brand.company_name || "",
    brand_logo: brand.logo || "",
    brand_name: brand.display_name || user?.name || "",
    brand_title: brand.title || "",
  });
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null); // industry name
  const [department, setDepartment] = useState(null);
  const [solution, setSolution] = useState("Blueprint");
  const [orgSize, setOrgSize] = useState(user?.account_type || "small");
  useEffect(() => { if (user?.account_type) setOrgSize(user.account_type); }, [user]);
  const orgSizeLabel = ORG_SIZES.find((o) => o.key === orgSize)?.label || "Small Org";
  const chooseOrgSize = async (key) => {
    setOrgSize(key);
    if (user) {
      try { await api.post("/account/type", { account_type: key }); setUser((u) => u ? { ...u, account_type: key } : u); }
      catch (_) { /* non-blocking */ }
    }
  };
  const engineLocation = useLocation();
  const industryDeepLinked = useRef(false);
  useEffect(() => {
    const s = new URLSearchParams(engineLocation.search).get("solution");
    if (s && SOLUTIONS.some((x) => x.key === s)) setSolution(s);
  }, [engineLocation.search]);
  const [assetType, setAssetType] = useState("Logo");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [compliance, setCompliance] = useState(null);
  const [pricing, setPricing] = useState(null);
  useEffect(() => {
    api.get("/engine/pricing", { params: { country } }).then(({ data }) => setPricing(data)).catch(() => {});
  }, [country]);
  const priceFor = (key) => (pricing?.prices?.[key] ?? null);
  const [countrySearch, setCountrySearch] = useState("");
  const [regionText, setRegionText] = useState("");
  const [useRegion, setUseRegion] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [customPrompt, setCustomPrompt] = useState("");
  const [downloading, setDownloading] = useState("");
  // Domain prompt suggestions + reference files
  const [suggests, setSuggests] = useState([]);
  const [suggBusy, setSuggBusy] = useState(false);
  const [suggQuery, setSuggQuery] = useState("");
  const [shownTitles, setShownTitles] = useState([]);
  const [refBusy, setRefBusy] = useState(false);
  const [refContext, setRefContext] = useState("");
  const [refFiles, setRefFiles] = useState(0);
  const [refImages, setRefImages] = useState(0);
  const [refNames, setRefNames] = useState([]);
  const refInputRef = useRef(null);
  const promptRef = useRef(null);
  const autoPromptRef = useRef("");
  const [appCode, setAppCode] = useState("");
  const [codeView, setCodeView] = useState("preview");
  const [appSession, setAppSession] = useState(null);
  const [appBuilding, setAppBuilding] = useState(false);
  const buildCancelRef = useRef(false);
  useEffect(() => () => { buildCancelRef.current = true; }, []);
  const [refineText, setRefineText] = useState("");
  const [refineBusy, setRefineBusy] = useState(false);
  const [savingOut, setSavingOut] = useState(false);
  const [savedOutId, setSavedOutId] = useState("");
  const [outCategory, setOutCategory] = useState("auto");
  const [addBusy, setAddBusy] = useState("");

  const refineApp = async () => {
    if (!gate()) return;
    if (!refineText.trim()) { toast.error("Describe the change you want."); return; }
    setRefineBusy(true);
    try {
      const { data } = await api.post("/software/refine", { instruction: refineText.trim(), current_html: appCode, session_id: appSession, black_bg: blackBg, country });
      setAppCode(data.app_html || appCode);
      setRefineText("");
      setCodeView("preview");
      if (typeof data.credits_remaining === "number" && setUser) setUser((u) => (u ? { ...u, credits: data.credits_remaining } : u));
      toast.success(`Updated your app (−${data.cost} credits)`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setRefineBusy(false); }
  };
  const [domainQuery, setDomainQuery] = useState("");
  const [domains, setDomains] = useState([]);
  const [domainBusy, setDomainBusy] = useState(false);

  const searchDomains = async () => {
    if (!gate()) return;
    if (!domainQuery.trim()) { toast.error("Type a domain to search (e.g. yourbrand.com)."); return; }
    setDomainBusy(true);
    try {
      const { data } = await api.post("/host/domains", { query: domainQuery.trim() });
      setDomains(data.domains || []);
      if (data.simulated) toast.info("Showing simulated results — connect Hostinger to search live.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setDomainBusy(false); }
  };

  const hostOnDomain = async (domain) => {
    if (!gate()) return;
    try {
      const { data } = await api.post("/host/deploy", { domain, html: appCode });
      toast.success(data.message || `Deploying to ${domain}…`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    setAppCode(result?.content?.app_html || "");
    setAppSession(result?.app_session_id || null);
    setCodeView("preview");
    setSavedOutId("");
  }, [result]);

  // Auto-load domain+function specific prompts for the selected industry & solution
  useEffect(() => {
    if (selected) { setSuggQuery(""); loadSuggests(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, solution]);

  const loadSuggests = async (more = false) => {
    if (!selected) { toast.error("Pick an industry first."); return; }
    setSuggBusy(true);
    try {
      const { data } = await api.post("/prompts/suggest", {
        industry: selected, solution_type: solution, query: suggQuery,
        exclude: more ? shownTitles : [], count: 5,
      });
      const items = data.items || [];
      setSuggests(more ? [...suggests, ...items] : items);
      setShownTitles((prev) => [...(more ? prev : []), ...items.map((i) => i.title)]);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSuggBusy(false); }
  };

  const applySuggest = (it) => {
    setCustomPrompt(it.prompt);
    setTemplateId("");
    toast.success(`Loaded: ${it.title}`);
    setTimeout(() => {
      const el = promptRef.current;
      if (el) { el.focus(); const n = el.value.length; el.setSelectionRange(n, n); el.scrollTop = el.scrollHeight; }
    }, 30);
  };
  const applyProject = (proj) => {
    const loc = locationLabel();
    const subject = isGov && department ? `${department} (Government & Public Sector)` : (selected || "your organization");
    const p = `Create a ${solution} — "${proj}" — for a ${subject} in ${loc}, sized for ${orgSizeLabel}.${orgSuffix()}`;
    setCustomPrompt(p);
    autoPromptRef.current = p;
    setTemplateId("");
    toast.success(`Loaded: ${proj}`);
  };
  const saveSuggest = async (it) => {
    if (!user) { toast.error("Sign in to save prompts to your library."); return; }
    try {
      await api.post("/prompts", { title: it.title, description: it.description, prompt: it.prompt, industry: selected, solution_type: solution });
      toast.success("Saved to your prompt library");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const onRefFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (!gate()) { if (refInputRef.current) refInputRef.current.value = ""; return; }
    setRefBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/blueprint/reference", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRefContext(data.reference_context || "");
      setRefFiles(data.files || files.length);
      setRefImages(data.images || 0);
      setRefNames(files.map((f) => f.name));
      toast.success("Reference files read — My Kind will use them.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setRefBusy(false); if (refInputRef.current) refInputRef.current.value = ""; }
  };
  const clearRefs = () => { setRefContext(""); setRefFiles(0); setRefImages(0); setRefNames([]); };

  const exportFile = async (fmt) => {
    if (!result) return;
    if (!gate()) return;
    setDownloading(fmt);
    try {
      const label = result.deliverable_label || result.solution_type || "My Kind";
      const { data } = await api.post("/export", {
        title: `${label} — ${result.industry || selected || "My Kind"}`,
        deliverable_label: label,
        region: result.location_label || result.region || "",
        department: result.department || null,
        frameworks: result.frameworks || [],
        content: result.content || {},
        format: fmt,
        site_url: window.location.origin,
        ...brandFields(),
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${label}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${label}.${fmt}`);
    } catch (e) {
      toast.error("Could not generate the file. Please try again.");
    } finally { setDownloading(""); }
  };

  const shareText = () => {
    const label = result?.deliverable_label || "output";
    return `Check out my ${label} for ${result?.industry || selected || "my project"}, made with My Kind — the First 6th-Generation Global App.`;
  };
  const shareTo = (network) => {
    const text = encodeURIComponent(shareText());
    const site = encodeURIComponent(window.location.origin);
    const urls = {
      whatsapp: `https://wa.me/?text=${text}%20${site}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${site}&quote=${text}`,
      x: `https://twitter.com/intent/tweet?text=${text}&url=${site}`,
    };
    window.open(urls[network], "_blank", "noopener,noreferrer");
  };
  const emailShare = () => {
    const label = result?.deliverable_label || "My Kind output";
    const subject = encodeURIComponent(`${label} — made with My Kind`);
    const body = encodeURIComponent(`${shareText()}\n\n${result?.content?.summary || ""}\n\nMade with My Kind: ${window.location.origin}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const industries = meta?.industries || [];
  const countries = meta?.countries || [];
  const departments = meta?.government_departments || [];
  const templatesBySolution = meta?.prompt_templates || {};
  const isGov = selected === "Government & Public Sector";
  const filteredCountries = countries.filter((c) => c.name.toLowerCase().includes(countrySearch.toLowerCase()));

  const templates = templatesBySolution[solution] || [];
  const substitute = (tpl) => {
    const subject = isGov && department ? `${selected} — ${department}` : (selected || "your organization");
    const loc = countryObj?.name || "your country";
    return (tpl || "").replaceAll("{industry}", subject).replaceAll("{location}", loc);
  };
  const applyTemplate = (id) => {
    setTemplateId(id);
    const tpl = templates.find((t) => t.id === id);
    setCustomPrompt(tpl ? substitute(tpl.prompt) : "");
  };

  const locationLabel = () => (useRegion ? (regionText.trim() || "your market") : (countryObj?.name || "your market"));
  const orgSuffix = () => ` Tailor the scope, budget and complexity to ${ORG_SIZE_DESC[orgSize] || ORG_SIZE_DESC.small}.`;
  const buildContextPrompt = () => {
    const loc = locationLabel();
    const subject = isGov && department ? `${department} (Government & Public Sector)` : (selected || "your organization");
    if (solution === "Blueprint") return `Architect a compliance-ready blueprint for a ${subject} organization in ${loc}. Cover regulatory frameworks, key requirements, proposed architecture, risks & mitigations, and a delivery timeline.`;
    if (solution === "Software") return `Build an experiential ${subject} software application for ${loc}. Define the core features, the tech stack, and produce a working, deployable app.`;
    if (solution === "Marketing") return `Create a ${assetType} for a ${subject} brand in ${loc}. Make it on-brand, conversion-focused and ready to use.`;
    if (solution === "Management Consulting") return `Develop a management-consulting roadmap for a ${subject} organization in ${loc}. Include diagnostics, prioritized initiatives, KPIs and a 90-day action plan.`;
    if (solution === "Insights & Signals") return `Produce a combined Insights & Signals report for the ${subject} sector in ${loc}. Part 1 — data-driven insights: key trends, metrics, opportunities and clear, actionable recommendations. Part 2 — live signals: the strongest emerging & weak signals and market/tech/regulatory shifts, with what each signal means, how strong it is, and the recommended action.`;
    if (solution === "SWOT Report") return `Build a rigorous SWOT report for a ${subject} organization in ${loc}. Detail Strengths, Weaknesses, Opportunities and Threats with evidence and strategic implications.`;
    if (solution === "Competitive Intelligence") return `Run a competitive-intelligence deep-dive for the ${subject} sector in ${loc}. Profile the key players, their positioning, pricing, strengths & gaps, and how to win.`;
    return `Create a ${solution} for a ${subject} organization in ${loc}.`;
  };
  // Precise, contextual prompt from Industry + Activity + Geography (unless the user typed or loaded a suggestion)
  useEffect(() => {
    if (!selected) return;
    if (!customPrompt.trim() || customPrompt === autoPromptRef.current) {
      const p = buildContextPrompt() + orgSuffix();
      autoPromptRef.current = p;
      setCustomPrompt(p);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, solution, country, useRegion, regionText, department, assetType, orgSize]);

  const tweakedEnough = customPrompt.trim().length >= 40 && customPrompt.trim() !== autoPromptRef.current;
  const promptTitle = () => (customPrompt.trim().split("\n")[0].slice(0, 80)) || `${selected || "My Kind"} ${solution} prompt`;
  const addToLibrary = async () => {
    if (!user) { toast.error("Sign in to save prompts to your library."); return; }
    setAddBusy("lib");
    try {
      await api.post("/prompts", { title: promptTitle(), description: `${selected || ""} · ${solution}`, prompt: customPrompt.trim(), industry: selected || "", solution_type: solution });
      toast.success("Added to your prompt library");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setAddBusy(""); }
  };
  const buildOutputText = () => {
    const c = result?.content || {};
    const L = [];
    const lbl = result?.deliverable_label || result?.solution_type || "Output";
    L.push(`# ${lbl} — ${result?.industry || selected || ""} (${result?.location_label || result?.region || ""})`);
    if (c.summary) L.push(`\n## Executive Summary\n${c.summary}`);
    if (result?.frameworks?.length) L.push(`\n## Regulatory Frameworks\n- ${result.frameworks.join("\n- ")}`);
    if (c.requirements?.length) L.push(`\n## Key Requirements\n- ${c.requirements.join("\n- ")}`);
    if (c.compliance?.length) L.push(`\n## Compliance & Regulatory\n- ${c.compliance.join("\n- ")}`);
    if (c.architecture?.length) L.push(`\n## ${result?.solution_type === "Software" ? "Tech Stack" : "Architecture"}\n- ${c.architecture.join("\n- ")}`);
    if (c.risks?.length) L.push(`\n## Risks & Mitigations\n${c.risks.map((r) => `- ${r.risk} → ${r.mitigation}`).join("\n")}`);
    if (c.build_prompts?.length) L.push(`\n## Build Prompts\n${c.build_prompts.map((p, i) => `${i + 1}. ${p}`).join("\n")}`);
    if (c.timeline_weeks) L.push(`\n## Timeline\n${c.timeline_weeks} weeks`);
    if (c.recommendation) L.push(`\n## Recommendation\n${c.recommendation}`);
    if (appCode) L.push(`\n## App (HTML)\n${appCode.slice(0, 20000)}`);
    return L.join("\n");
  };
  const saveOutput = async () => {
    if (!user) { toast.error("Sign in to save & earn from your output."); return; }
    if (!result) return;
    setSavingOut(true);
    try {
      const { data } = await api.post("/outputs/save", {
        title: `${result.deliverable_label || result.solution_type} — ${result.industry || selected || "My Kind"}`,
        text: buildOutputText(),
        solution_type: result.solution_type || solution,
        deliverable_label: result.deliverable_label || result.solution_type || solution,
        industry: result.industry || selected || "",
        region: result.location_label || result.region || "",
        content: result.content || {},
        category: outCategory === "auto" ? undefined : outCategory,
      });
      setSavedOutId(data.id);
      toast.success("Saved to My Kind — you'll earn 15% each time someone opens it.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingOut(false); }
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return industries;
    return industries.filter((i) => i.name.toLowerCase().includes(q) || i.category.toLowerCase().includes(q));
  }, [query, industries]);

  const openIndustry = (name) => {
    setSelected(name);
    setDepartment(null);
    setResult(null);
    setCompliance(null);
    setDialogOpen(true);
  };

  // Deep link from the Landing marquee: /engine?industry=<name> → open that industry
  useEffect(() => {
    const ind = new URLSearchParams(engineLocation.search).get("industry");
    if (!ind || industryDeepLinked.current || industries.length === 0) return;
    const lower = ind.trim().toLowerCase();
    const match = industries.find((x) => x.name.toLowerCase() === lower)
      || industries.find((x) => x.name.toLowerCase().includes(lower) || lower.includes(x.name.toLowerCase()));
    if (match) { industryDeepLinked.current = true; openIndustry(match.name); }
  }, [engineLocation.search, industries]);

  useEffect(() => {
    if (!dialogOpen || !selected) return;
    const params = { industry: selected };
    if (useRegion && regionText.trim()) params.region = regionText.trim();
    else if (country) params.country = country;
    api.get("/compliance", { params }).then(({ data }) => setCompliance(data)).catch(() => setCompliance(null));
  }, [dialogOpen, selected, country, useRegion, regionText]);

  const generate = async () => {
    if (!gate()) return;
    const region = useRegion ? regionText.trim() : "";
    if (!useRegion && !country) { toast.error("Choose a country, or switch to Region and type one (e.g. Global)."); return; }
    if (useRegion && !region) { toast.error("Type a region (e.g. Global, EU, California)."); return; }
    if (isGov && !department) { toast.error("Please choose a government department."); return; }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/blueprint/generate", {
        industry: selected, country: useRegion ? null : country, region,
        department: isGov ? department : null,
        solution_type: solution, asset_type: solution === "Marketing" ? assetType : null,
        custom_prompt: customPrompt || null,
        org_size: orgSize,
        reference_context: refContext || null,
        reference_files: refFiles,
        reference_images: refImages,
        black_bg: blackBg,
      });
      setResult(data.blueprint);
      if (data.credits_remaining != null && user) setUser({ ...user, credits: data.credits_remaining });
      toast.success(`My Kind architected your ${solution.toLowerCase()}`);
      if (data.blueprint?.app_pending && data.blueprint?.app_session_id) {
        buildApp(data.blueprint);
      }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "That took too long. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const buildApp = async (bp) => {
    setAppBuilding(true);
    buildCancelRef.current = false;
    try {
      const c = bp.content || {};
      const ctx = [
        `Domain/industry: ${bp.industry}`,
        `Location: ${bp.location_label || ""}`,
        c.requirements?.length ? "Features: " + c.requirements.slice(0, 10).join("; ") : "",
        c.compliance?.length ? "Compliance to honor: " + c.compliance.slice(0, 8).join("; ") : "",
      ].filter(Boolean).join("\n");
      const instruction = (bp.custom_prompt || `A ${bp.industry} software application`).trim();
      const { data } = await api.post("/software/build", { instruction, context: ctx, session_id: bp.app_session_id, black_bg: blackBg });
      const jobId = data.job_id;
      if (!jobId) { setAppCode(data.app_html || ""); return; }
      // Poll until ready (build can take 60-120s, beyond the gateway limit for a single call)
      for (let i = 0; i < 90; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        if (buildCancelRef.current) return;
        try {
          const { data: s } = await api.get(`/software/build/${jobId}`);
          if (buildCancelRef.current) return;
          if (s.status === "ready") {
            setAppCode(s.app_html || "");
            if (s.cost && user) setUser((u) => u ? { ...u, credits: Math.max(0, (u.credits || 0) - s.cost) } : u);
            return;
          }
          if (s.status === "error") { toast.error(s.error || "Build failed — tap Rebuild."); return; }
        } catch (_) { /* keep polling */ }
      }
      if (!buildCancelRef.current) toast.error("The app is taking longer than usual — please try again.");
    } catch (e) {
      if (!buildCancelRef.current) toast.error("Couldn't start the app build. Please try again.");
    } finally {
      setAppBuilding(false);
    }
  };

  return (
    <div className={`max-w-7xl mx-auto px-5 sm:px-8 ${embedded ? "pt-4 pb-10" : "pt-28 pb-24"}`}>
      <div className="max-w-3xl">
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter">{t("engine_title")}</h1>
        <p className="text-sec mt-3">For business, government and organizations — turn any goal into action. Blueprints, compliant software, strategy and marketing, end to end.</p>
      </div>

      {!embedded && <TrendingStrip scope="business" title="Trending in Business" limit={4} />}

      {/* Step 1 — choose deliverable */}
      <div className="mt-8" data-testid="engine-solution-picker">
        <div className="text-xs uppercase tracking-widest text-sec mb-2">Step 1 — What do you want to create?</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {SOLUTIONS.map((s) => {
            const active = solution === s.key;
            return (
              <button key={s.key} data-testid={`engine-solution-${s.key}`}
                onClick={() => { setSolution(s.key); setTemplateId(""); setCustomPrompt(""); }}
                className={`relative rounded-xl p-3 text-left border transition-all duration-200 ${active ? "border-brand bg-brand/15 ring-2 ring-brand shadow-lg shadow-brand/10 -translate-y-0.5" : "border-rud hover:border-brand/50 hover:bg-brand/5"}`}>
                {active && <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-brand text-black grid place-items-center"><CheckCircle2 size={13} /></span>}
                <div className="flex items-center gap-2">
                  <s.icon size={18} className={`shrink-0 ${active ? "text-brand" : "text-brand/80"}`} />
                  <div className={`text-sm font-medium ${active ? "text-brand" : ""}`}>{s.key}</div>
                </div>
                <div className="text-[11px] text-sec leading-tight mt-1">{s.desc}</div>
                {priceFor(s.key) != null && (
                  <div className="text-[11px] font-semibold text-brand mt-1.5" data-testid={`engine-price-${s.key}`}>{priceFor(s.key)} credits</div>
                )}
              </button>
            );
          })}
          <Link to="/compliance" data-testid="engine-solution-compliance"
            className="relative rounded-xl p-3 text-left border border-brand/40 bg-brand/5 hover:border-brand hover:bg-brand/10 transition-all duration-200">
            <div className="flex items-center gap-2">
              <ShieldCheck size={18} className="text-brand shrink-0" />
              <div className="text-sm font-medium">Compliance Engine</div>
            </div>
            <div className="text-[11px] text-sec leading-tight mt-1">Decode any law in plain English — risks, must-haves & a fix-it plan.</div>
            <div className="text-[11px] font-semibold text-brand mt-1.5">from 100 credits</div>
          </Link>
        </div>
        <div className="mt-2.5 flex items-center gap-2 text-[11px]" data-testid="engine-next-tip">
          <span className="px-2 py-0.5 rounded-full bg-brand text-black font-semibold uppercase tracking-wide shrink-0">Next</span>
          <span className="text-sec">
            {!selected
              ? <>You chose <b className="text-[var(--rud-text)]">{solution}</b> — now pick your <b className="text-[var(--rud-text)]">industry in Step&nbsp;2</b> below to continue.</>
              : <>Great — <b className="text-[var(--rud-text)]">{solution}</b> for <b className="text-[var(--rud-text)]">{selected}</b> is set. Scroll down and hit <b className="text-[var(--rud-text)]">Generate</b>.</>}
          </span>
        </div>
      </div>

      {/* Step 1.5 — organization size */}
      <div className="mt-6" data-testid="engine-orgsize-picker">
        <div className="text-xs uppercase tracking-widest text-sec mb-2 flex items-center gap-1.5"><Briefcase size={13} className="text-brand" /> Step 1.5 — Who is this for?</div>
        <div className="flex flex-wrap items-center gap-2">
          {ORG_SIZES.map((o) => (
            <button key={o.key} data-testid={`orgsize-${o.key}`} onClick={() => chooseOrgSize(o.key)}
              className={`rounded-xl px-4 py-2.5 text-left border transition-colors ${orgSize === o.key ? "border-brand bg-brand/10 text-brand" : "border-rud text-sec hover:border-brand/50"}`}>
              <div className="text-sm font-medium">{o.label}</div>
              <div className="text-[11px] text-sec leading-tight">{o.desc}</div>
            </button>
          ))}
          <Link to="/ventures" data-testid="engine-solution-crowdfunding"
            className="ml-auto rounded-xl px-4 py-2.5 text-left border border-brand/40 bg-brand/5 hover:border-brand transition-colors flex items-center gap-2">
            <Rocket size={18} className="text-brand shrink-0" />
            <div>
              <div className="text-sm font-medium">Crowdfund</div>
              <div className="text-[11px] text-sec leading-tight">Raise, sell or invest</div>
            </div>
          </Link>
        </div>
        <p className="text-[11px] text-sec mt-1.5">My Kind tailors scope, budget and complexity to your organization size.</p>
      </div>

      {/* Step 2 — pick industry */}
      <div className="mt-6 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-sec mb-2">Step 2 — Pick your industry</div>
          <div className="relative w-full sm:w-96">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-sec" />
            <input
              data-testid="industry-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("search_industries")}
              className="rud-input w-full rounded-full pl-11 pr-4 py-3 text-sm"
            />
          </div>
        </div>
        <div className="w-full sm:w-72">
          <select data-testid="engine-industry-quick" value="" data-no-translate translate="no" onChange={(e) => e.target.value && openIndustry(e.target.value)}
            className="rud-input w-full rounded-lg px-3 py-2.5 text-sm">
            <option value="">Quick select an industry…</option>
            {industries.map((i) => <option key={i.name} value={i.name}>{i.name}</option>)}
          </select>
          <p className="text-[11px] text-sec mt-1">Building <b className="text-brand">{{ Blueprint: "a Blueprint", Software: "an experiential app", Marketing: "Marketing collateral", "Management Consulting": "a consulting roadmap" }[solution] || "your deliverable"}</b> — pick an industry to start.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-6">
        {filtered.map((ind, i) => (
          <motion.button
            key={ind.name}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.02, 0.4) }}
            onClick={() => openIndustry(ind.name)}
            data-testid={`industry-${ind.name}`}
            className="rud-card rounded-xl p-4 text-left group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-elevated grid place-items-center text-brand shrink-0 group-hover:scale-110 transition-transform">
                <Icon name={ind.icon} size={20} />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium leading-tight truncate">{ind.name}</div>
                <div className="text-xs text-sec mt-0.5 truncate">{ind.category}</div>
              </div>
            </div>
          </motion.button>
        ))}
        {filtered.length === 0 && <p className="text-sec col-span-full py-10 text-center">No industries match “{query}”.</p>}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="glass border-rud text-[var(--rud-text)] max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="blueprint-dialog"
          onFocusOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => { if (downloading) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl tracking-tight">{selected}</DialogTitle>
          </DialogHeader>

          {compliance && compliance.combined?.length > 0 && (
            <div className="rounded-xl border border-brand/30 bg-brand/5 p-3.5" data-testid="industry-compliance">
              <div className="text-[11px] uppercase tracking-widest text-brand flex items-center gap-1.5"><ShieldCheck size={13} /> Compliance incorporated{compliance.region ? ` · ${compliance.region}` : ""}</div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {[...new Set(compliance.combined.map((c) => String(c).trim()))].map((c, i) => (
                  <Link key={i} data-testid={`compliance-chip-${i}`} to={`/compliance?name=${encodeURIComponent(c)}`}
                    title={`Interpret ${c} in the Compliance Engine`}
                    className="text-[11px] px-2.5 py-1 rounded-full font-medium inline-flex items-center gap-1 transition-transform hover:scale-105 hover:opacity-90 cursor-pointer"
                    style={{ backgroundColor: "var(--rud-text)", color: "var(--rud-bg)" }}>{c}<ArrowRight size={10} className="-rotate-45 opacity-70" /></Link>
                ))}
              </div>
              <p className="text-[11px] text-sec mt-2">Tap any standard to open it in the <b className="text-brand">Compliance Engine</b> — plain-English interpretation, risks & mitigations. My Kind builds these into your {selected} deliverable.</p>
            </div>
          )}

          {!result && (
            <div className="space-y-5">
              {isGov && (
                <div>
                  <label className="text-xs uppercase tracking-widest text-sec">Government Department</label>
                  <Select value={department || ""} onValueChange={setDepartment}>
                    <SelectTrigger data-testid="department-select" className="rud-input rounded-lg mt-2"><SelectValue placeholder="Select department" /></SelectTrigger>
                    <SelectContent className="glass border-rud text-[var(--rud-text)] max-h-72">
                      {departments.map((d) => <SelectItem key={d.name} value={d.name} className="focus:bg-white/10">{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="rounded-xl border border-rud bg-elevated/40 p-3.5" data-testid="engine-geography">
                <label className="text-xs uppercase tracking-widest text-sec flex items-center gap-1.5"><Globe2 size={13} className="text-brand" /> Geography / market</label>
                <p className="text-[11px] text-sec mt-0.5">Pick a country, a continent/region, or go worldwide — My Kind tailors the output to that scope.</p>
                {(() => {
                  const globalActive = useRegion && regionText.trim().toLowerCase() === "global";
                  const regionActive = useRegion && !globalActive;
                  const mode = (id, label, icon) => {
                    const active = id === "country" ? !useRegion : id === "global" ? globalActive : regionActive;
                    const onClick = () => id === "country" ? setUseRegion(false)
                      : id === "global" ? (setUseRegion(true), setRegionText("Global"))
                      : (setUseRegion(true), regionText.trim().toLowerCase() === "global" && setRegionText(""));
                    return (
                      <button data-testid={`loc-mode-${id}`} onClick={onClick}
                        className={`flex-1 px-3 py-1.5 rounded-full transition-colors flex items-center justify-center gap-1.5 ${active ? "bg-brand text-black font-semibold" : "text-sec hover:text-[var(--rud-text)]"}`}>{icon}{label}</button>
                    );
                  };
                  return (
                    <>
                      <div className="flex gap-1 p-0.5 rounded-full bg-elevated border border-rud text-[11px] mt-2.5">
                        {mode("country", "Country", <MapPin size={12} />)}
                        {mode("region", "Region / Continent", <LayoutGrid size={12} />)}
                        {mode("global", "Global", <Globe2 size={12} />)}
                      </div>
                      {!useRegion ? (
                        <Select value={country} onValueChange={setCountry}>
                          <SelectTrigger data-testid="country-select" className="rud-input rounded-lg mt-3">
                            <SelectValue placeholder="Choose country">
                              {countryObj ? <span>{countryObj.flag} {countryObj.name}</span> : "Choose country"}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="glass border-rud text-[var(--rud-text)] max-h-72">
                            <div className="p-2 sticky top-0 bg-[var(--rud-surface)]">
                              <input data-testid="engine-country-search" value={countrySearch} onChange={(e) => setCountrySearch(e.target.value)} placeholder="Search country…" className="rud-input w-full rounded-md px-2 py-1.5 text-xs" onKeyDown={(e) => e.stopPropagation()} />
                            </div>
                            {filteredCountries.map((c) => <SelectItem key={c.code} value={c.code} className="focus:bg-white/10">{c.flag} {c.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : globalActive ? (
                        <div className="mt-3 flex items-center gap-2.5 rounded-lg border border-brand/40 bg-brand/5 px-3.5 py-3">
                          <Globe2 size={22} className="text-brand shrink-0" />
                          <div><div className="text-sm font-medium">Worldwide scope</div><div className="text-[11px] text-sec">Output is written for a global, multi-market audience.</div></div>
                        </div>
                      ) : (
                        <div className="mt-3">
                          <input data-testid="engine-region-input" value={regionText} onChange={(e) => setRegionText(e.target.value)} placeholder="Type a continent or region — Europe, Asia Pacific, Southeast Asia, California…" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
                          <div className="flex flex-wrap gap-2 mt-2">
                            {["North America", "Latin America", "Europe", "European Union", "Africa", "Middle East", "Asia", "Asia Pacific", "Oceania"].map((r) => (
                              <button key={r} data-testid={`region-quick-${r}`} onClick={() => setRegionText(r)} className={`text-[11px] rounded-full px-3 py-1 border transition-colors ${regionText === r ? "border-brand text-brand bg-brand/10" : "border-rud text-sec hover:border-brand"}`}>{r}</button>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-sec">{t("solution_type")}</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                  {SOLUTIONS.map((s) => (
                    <button
                      key={s.key}
                      data-testid={`solution-${s.key}`}
                      onClick={() => { setSolution(s.key); setTemplateId(""); setCustomPrompt(""); }}
                      className={`rounded-lg p-3 text-left border transition-colors ${solution === s.key ? "border-brand bg-white/5" : "border-rud"}`}
                    >
                      <div className="flex items-center gap-2">
                        <s.icon size={18} className="text-brand shrink-0" />
                        <div className="text-sm font-medium">{s.key}</div>
                      </div>
                      <div className="text-[11px] text-sec leading-tight mt-1">{s.desc}</div>
                      {priceFor(s.key) != null && (
                        <div className="text-[11px] font-semibold text-brand mt-1" data-testid={`solution-price-${s.key}`}>{priceFor(s.key)} credits</div>
                      )}
                    </button>
                  ))}
                  <Link to="/compliance" data-testid="solution-compliance"
                    className="rounded-lg p-3 text-left border border-brand/40 bg-brand/5 hover:border-brand transition-colors block">
                    <div className="flex items-center gap-2">
                      <ShieldCheck size={18} className="text-brand shrink-0" />
                      <div className="text-sm font-medium">Compliance</div>
                    </div>
                    <div className="text-[11px] text-sec leading-tight mt-1">Interpret global regulations & build a risk/mitigation plan.</div>
                    <div className="text-[11px] font-semibold text-brand mt-1">from 100 credits</div>
                  </Link>
                </div>
              </div>

              {solution === "Marketing" && (
                <div>
                  <label className="text-xs uppercase tracking-widest text-sec">Marketing asset</label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {MARKETING_ASSETS.map((a) => (
                      <button
                        key={a}
                        data-testid={`asset-${a}`}
                        onClick={() => setAssetType(a)}
                        className={`rounded-lg py-2 text-sm border transition-colors ${assetType === a ? "border-brand bg-white/5 text-brand" : "border-rud text-sec"}`}
                      >
                        {a}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div data-testid="suggested-projects">
                <label className="text-xs uppercase tracking-widest text-sec flex items-center gap-1.5"><Sparkles size={13} className="text-brand" /> Suggested projects · {orgSizeLabel}</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(SUGGESTED_PROJECTS[orgSize] || SUGGESTED_PROJECTS.small).map((proj) => (
                    <button key={proj} data-testid={`suggested-project-${proj}`} onClick={() => applyProject(proj)}
                      className="text-xs rounded-full px-3 py-1.5 border border-rud text-sec hover:border-brand hover:text-brand transition-colors">{proj}</button>
                  ))}
                </div>
                <p className="text-[11px] text-sec mt-1.5">Tap one to load a ready prompt tailored to a {orgSizeLabel.toLowerCase()}.</p>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs uppercase tracking-widest text-sec flex items-center gap-1.5"><Sparkles size={13} className="text-brand" /> Domain prompts</label>
                  <span className="text-[11px] text-sec">Editable · output follows your prompt</span>
                </div>

                <div className="flex gap-2 mt-2">
                  <input data-testid="prompt-search" value={suggQuery} onChange={(e) => setSuggQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadSuggests(false)} placeholder={`Search ${selected || "domain"} prompts…`} className="rud-input flex-1 rounded-lg px-3 py-2 text-sm" />
                  <button data-testid="prompt-suggest-btn" onClick={() => loadSuggests(false)} disabled={suggBusy || !selected} className="btn-ghost rounded-lg h-10 px-3 text-sm flex items-center gap-1.5 disabled:opacity-50">
                    {suggBusy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Suggest
                  </button>
                </div>

                {/* Prompt box FIRST — precise text from your Industry, Activity & Geography */}
                <textarea
                  data-testid="custom-prompt"
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  rows={4}
                  placeholder="A precise prompt appears here from your Industry, Activity & Geography — tweak it, or write your own. My Kind produces exactly what you ask."
                  className="rud-input w-full rounded-lg px-3 py-2 mt-3 text-sm resize-none"
                  ref={promptRef}
                />
                {tweakedEnough && (
                  <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="prompt-add-row">
                    <button data-testid="prompt-add-library" onClick={addToLibrary} disabled={addBusy === "lib"} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-1.5 disabled:opacity-50">
                      {addBusy === "lib" ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add to library
                    </button>
                    <Link to="/prosperity" data-testid="prompt-mint-hint" className="text-[11px] text-sec inline-flex items-center gap-1 hover:text-brand">
                      Want royalties? Mint this in <b className="text-brand">Prosperity-Mint Money</b> <ArrowRight size={12} />
                    </Link>
                  </div>
                )}

                <div className="mt-4 mb-1">
                  <label className="text-[11px] uppercase tracking-widest text-sec">Or pick a domain prompt</label>
                </div>
                {suggests.length > 0 ? (
                  <div className="space-y-2" data-testid="prompt-suggestions">
                    {suggests.map((it, i) => (
                      <div key={i} className="rounded-lg bg-elevated border border-rud p-3" data-testid={`prompt-suggestion-${i}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="text-sm font-medium">{it.title}</div>
                            <div className="text-[11px] text-sec mt-0.5">{it.description}</div>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <button data-testid={`prompt-use-${i}`} onClick={() => applySuggest(it)} className="text-xs rounded-full border border-brand text-brand px-2.5 py-1 hover:bg-brand/10">Use</button>
                            <button data-testid={`prompt-save-${i}`} onClick={() => saveSuggest(it)} title="Save to library" className="text-sec hover:text-brand px-1"><Bookmark size={14} /></button>
                          </div>
                        </div>
                      </div>
                    ))}
                    <button data-testid="prompt-more" onClick={() => loadSuggests(true)} disabled={suggBusy} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-1.5 disabled:opacity-50">
                      {suggBusy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} More prompts
                    </button>
                  </div>
                ) : (
                  <p className="text-[11px] text-sec">Tap <b>Suggest</b> for domain + function specific prompts you can load and tweak.</p>
                )}

                {/* Reference files */}
                <div className="mt-3">
                  <input ref={refInputRef} type="file" multiple onChange={onRefFiles} className="hidden" data-testid="reference-file-input"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.md,.png,.jpg,.jpeg,.webp" />
                  <div className="flex items-center gap-2 flex-wrap">
                    <button data-testid="reference-upload-btn" onClick={() => refInputRef.current?.click()} disabled={refBusy} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-1.5 disabled:opacity-50">
                      {refBusy ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />} Attach reference files
                    </button>
                    {refFiles > 0 && (
                      <span className="text-[11px] text-sec flex items-center gap-1.5" data-testid="reference-chip">
                        {refFiles} file{refFiles > 1 ? "s" : ""} attached · {refNames.slice(0, 2).join(", ")}{refNames.length > 2 ? "…" : ""}
                        <button onClick={clearRefs} className="text-sec hover:text-brand"><X size={12} /></button>
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-sec mt-1.5">PDF, Word, Excel, PPT, images or text — My Kind reads them and shapes your {solution.toLowerCase()} around their content.</p>
                </div>
              </div>

              <button data-testid="generate-blueprint" onClick={generate} disabled={loading} className="btn-primary w-full h-12 flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? <><Loader2 size={18} className="animate-spin" /> Architecting…</> : <>{solution === "Marketing" ? `Create ${assetType}` : SOLUTIONS.find((s) => s.key === solution)?.label}{priceFor(solution) != null ? ` · ${priceFor(solution)} credits` : ""} <ArrowRight size={18} /></>}
              </button>
              <p className="text-xs text-sec text-center">{priceFor(solution) != null ? `${priceFor(solution)} credits — fair price for ${countryObj?.name || pricing?.country || "your region"}` : "Fixed price shown before you generate"}{user ? ` · you have ${Math.round(user.credits)}` : " · sign in to save & track"}{blackBg ? " · 🌱 1% off on Black BG" : ""}</p>
              {solution === "Software" && priceFor("Software Refine") != null && (
                <p className="text-[11px] text-sec text-center" data-testid="refine-price-note">Each refinement after preview costs {priceFor("Software Refine")} credits.</p>
              )}
            </div>
          )}

          <AnimatePresence>
            {result && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5" data-testid="blueprint-result">
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="px-2.5 py-1 rounded-full bg-elevated border border-rud">{result.location_label || result.region}</span>
                  {result.department && <span className="px-2.5 py-1 rounded-full bg-elevated border border-rud">{result.department}</span>}
                  <span className="px-2.5 py-1 rounded-full bg-brand text-black" data-testid="result-deliverable-label">{result.deliverable_label || result.solution_type}</span>
                </div>
                <p className="text-sm leading-relaxed">{result.content.summary}</p>

                <Section icon={ShieldCheck} title="Regulatory Frameworks">
                  <div className="flex flex-wrap gap-2">
                    {result.frameworks.map((f) => <span key={f} className="text-xs px-2.5 py-1 rounded-md bg-elevated border border-rud">{f}</span>)}
                  </div>
                </Section>

                <Section icon={CheckCircle2} title="Key Requirements">
                  <ul className="space-y-1.5">{result.content.requirements?.map((r, i) => <li key={i} className="text-sm text-sec flex gap-2"><CheckCircle2 size={15} className="text-brand mt-0.5 shrink-0" />{r}</li>)}</ul>
                </Section>

                {result.content.compliance?.length > 0 && (
                  <Section icon={ShieldCheck} title="Compliance & Regulatory Requirements">
                    <ul className="space-y-1.5" data-testid="compliance-list">{result.content.compliance.map((r, i) => <li key={i} className="text-sm text-sec flex gap-2"><ShieldCheck size={15} className="text-brand mt-0.5 shrink-0" />{r}</li>)}</ul>
                  </Section>
                )}

                {result.content.compliance_map?.length > 0 && (
                  <Section icon={ShieldCheck} title="Standards Applied — Section by Section">
                    <p className="text-xs text-sec mb-3">Exactly which compliance standard was incorporated where in this deliverable.</p>
                    <div className="space-y-2" data-testid="compliance-map">
                      {result.content.compliance_map.map((m, i) => (
                        <div key={i} data-testid={`compliance-map-${i}`} className="rounded-xl border border-brand/25 bg-brand/5 p-3.5">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-brand/20 text-brand">{m.standard}</span>
                            {m.section && <span className="text-[11px] text-sec">applied in <span className="text-[var(--rud-text)] font-medium">{m.section}</span></span>}
                          </div>
                          {m.how && <p className="text-sm text-sec mt-1.5 leading-relaxed">{m.how}</p>}
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                {result.solution_type === "Software" && (appBuilding || appCode) && (
                  <Section icon={MonitorSmartphone} title="Your working app — view, code & host">
                    {appBuilding && !appCode ? (
                      <BuildBreak label={(result.deliverable_label || "app").toLowerCase()} />
                    ) : (
                    <>
                    <div className="flex gap-1 p-1 rounded-full bg-elevated border border-rud w-fit mb-3">
                      <button data-testid="app-tab-preview" onClick={() => setCodeView("preview")} className={`px-4 py-1.5 rounded-full text-sm transition-colors ${codeView === "preview" ? "bg-brand text-black" : "text-sec"}`}>Preview</button>
                      <button data-testid="app-tab-code" onClick={() => setCodeView("code")} className={`px-4 py-1.5 rounded-full text-sm transition-colors ${codeView === "code" ? "bg-brand text-black" : "text-sec"}`}>Code</button>
                    </div>
                    {codeView === "preview" ? (
                      <div data-testid="app-preview" className="rounded-xl overflow-hidden border border-rud bg-white">
                        <iframe title="My Kind generated app" srcDoc={appCode} className="w-full" style={{ height: 460, border: "none" }} sandbox="allow-scripts allow-forms allow-modals allow-popups" />
                      </div>
                    ) : (
                      <textarea data-testid="app-code" value={appCode} onChange={(e) => setAppCode(e.target.value)} spellCheck={false}
                        className="w-full rounded-xl border border-rud bg-[#0d0d0d] text-[#e6e6e6] font-mono text-xs p-4" style={{ height: 460 }} />
                    )}
                    <div className="mt-3 flex gap-2" data-testid="app-refine">
                      <input data-testid="app-refine-input" value={refineText} onChange={(e) => setRefineText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !refineBusy && refineApp()} placeholder="Tell My Kind what to change — 'add a due-date field', 'make it dark mode', 'add charts'…" className="rud-input flex-1 rounded-full px-4 py-2.5 text-sm" />
                      <button data-testid="app-refine-btn" onClick={refineApp} disabled={refineBusy} className="btn-primary rounded-full h-10 px-5 text-sm flex items-center gap-1.5 disabled:opacity-60">
                        {refineBusy ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Refine
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <button data-testid="app-download" onClick={() => {
                        const blob = new Blob([appCode], { type: "text/html" });
                        const url = URL.createObjectURL(blob); const a = document.createElement("a");
                        a.href = url; a.download = "rudram-app.html"; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
                        toast.success("App downloaded");
                      }} className="btn-ghost rounded-full h-9 px-4 flex items-center gap-1.5 text-sm"><Download size={14} /> Download app (.html)</button>
                      <button data-testid="app-newtab" onClick={() => {
                        const w = window.open("", "_blank"); if (w) { w.document.write(appCode); w.document.close(); }
                      }} className="btn-ghost rounded-full h-9 px-4 flex items-center gap-1.5 text-sm"><ArrowRight size={14} /> Open in new tab</button>
                    </div>

                    {/* Host on a domain */}
                    <div className="mt-4 rounded-xl border border-rud bg-elevated p-4" data-testid="host-panel">
                      <div className="flex items-center gap-2 text-sm font-medium mb-1"><ShieldCheck size={15} className="text-brand" /> Host on your own domain</div>
                      <p className="text-xs text-sec mb-3">Search a domain and go live via Hostinger — My Kind deploys this app for you.</p>
                      <div className="flex gap-2">
                        <input data-testid="domain-search-input" value={domainQuery} onChange={(e) => setDomainQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchDomains()} placeholder="yourbrand.com" className="rud-input flex-1 rounded-lg px-3 py-2 text-sm" />
                        <button data-testid="domain-search-btn" onClick={searchDomains} disabled={domainBusy} className="btn-primary rounded-lg h-10 px-4 text-sm flex items-center gap-1.5 disabled:opacity-60">
                          {domainBusy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Search
                        </button>
                      </div>
                      {domains.length > 0 && (
                        <div className="mt-3 space-y-1.5" data-testid="domain-results">
                          {domains.map((d, i) => (
                            <div key={i} className="flex items-center justify-between rounded-lg bg-surface border border-rud px-3 py-2 text-sm" data-testid={`domain-${i}`}>
                              <span className="flex items-center gap-2">{d.domain} {d.available ? <span className="text-[10px] text-green-500 border border-green-500/40 rounded-full px-2 py-0.5">available</span> : <span className="text-[10px] text-sec border border-rud rounded-full px-2 py-0.5">taken</span>}</span>
                              <span className="flex items-center gap-3">{d.price && <span className="text-xs text-sec">{d.price}</span>}
                                {d.available && <button data-testid={`domain-host-${i}`} onClick={() => hostOnDomain(d.domain)} className="text-xs rounded-full border border-brand text-brand px-3 py-1 hover:bg-brand/10">Host here</button>}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    </>
                    )}
                  </Section>
                )}

                {result.content.build_prompts?.length > 0 && (
                  <Section icon={Wand2} title="Build Prompts — paste into an AI builder">
                    <div className="space-y-2" data-testid="build-prompts">
                      {result.content.build_prompts.map((p, i) => (
                        <div key={i} className="rounded-lg bg-elevated border border-rud p-3 flex gap-3 items-start">
                          <span className="text-brand text-xs font-semibold mt-0.5 shrink-0">{i + 1}</span>
                          <p className="text-sm flex-1">{p}</p>
                          <button data-testid={`copy-build-${i}`} onClick={() => { navigator.clipboard.writeText(p); toast.success("Prompt copied"); }} className="text-sec hover:text-brand shrink-0" title="Copy"><Copy size={14} /></button>
                        </div>
                      ))}
                      <button data-testid="copy-all-build" onClick={() => { navigator.clipboard.writeText(result.content.build_prompts.map((p, i) => `${i + 1}. ${p}`).join("\n\n")); toast.success("All build prompts copied"); }} className="btn-ghost rounded-full h-9 px-4 flex items-center gap-1.5 text-sm mt-1"><Copy size={14} /> Copy all prompts</button>
                    </div>
                  </Section>
                )}

                <Section icon={Layers} title={result.solution_type === "Software" ? "Tech Stack" : "Proposed Architecture"}>
                  <ul className="space-y-1.5">{result.content.architecture?.map((r, i) => <li key={i} className="text-sm text-sec flex gap-2"><Layers size={15} className="text-brand mt-0.5 shrink-0" />{r}</li>)}</ul>
                </Section>

                {result.content.risks?.length > 0 && (
                  <Section icon={AlertTriangle} title="Risks & Mitigations">
                    <div className="space-y-2">
                      {result.content.risks.map((r, i) => (
                        <div key={i} className="rounded-lg bg-elevated border border-rud p-3">
                          <div className="text-sm font-medium">{r.risk}</div>
                          <div className="text-xs text-sec mt-1">→ {r.mitigation}</div>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                <div className="flex items-center justify-between rounded-lg bg-elevated border border-rud p-4">
                  <div className="flex items-center gap-2 text-sm"><Clock size={16} className="text-brand" /> Est. timeline</div>
                  <div className="font-serif text-2xl">{result.content.timeline_weeks} weeks</div>
                </div>
                <p className="text-sm text-sec italic border-l-2 border-brand pl-3">{result.content.recommendation}</p>

                <div className="rounded-xl bg-elevated border border-rud p-4" data-testid="result-actions">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-2"><Sparkles size={14} /> Save &amp; Earn</div>
                  <p className="text-[11px] text-sec mb-3">Save this {String(result.deliverable_label || result.solution_type).toLowerCase()} to My Kind. Others reuse it for 5 credits and <b className="text-brand">you earn 15%</b> — no re-generation, no credits burned by them beyond the small reuse fee.</p>
                  <div className="flex items-center gap-1.5 mb-3" data-testid="output-category-picker">
                    <span className="text-[11px] text-sec mr-1">Show on:</span>
                    {[["auto", "Auto"], ["personal", "Personal"], ["professional", "Professional"], ["both", "Both"]].map(([k, lbl]) => (
                      <button key={k} data-testid={`out-cat-${k}`} onClick={() => setOutCategory(k)}
                        className={`text-[11px] rounded-full px-2.5 py-1 border transition-colors ${outCategory === k ? "border-brand bg-brand/10 text-brand" : "border-rud text-sec hover:border-brand/50"}`}>{lbl}</button>
                    ))}
                  </div>
                  <button data-testid="save-earn-output" onClick={saveOutput} disabled={savingOut} className="btn-primary h-10 px-5 text-sm flex items-center gap-2 disabled:opacity-60 mb-4">
                    {savingOut ? <Loader2 size={15} className="animate-spin" /> : (savedOutId ? <CheckCircle2 size={15} /> : <Bookmark size={15} />)} {savedOutId ? "Saved — earning enabled" : "Save & Earn 15%"}
                  </button>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-3"><Download size={14} /> Download your {result.deliverable_label || result.solution_type}</div>
                  <div className="flex flex-wrap gap-2">
                    {["pdf", "docx", "pptx", "html", "md", "txt"].map((fmt) => (
                      <button key={fmt} data-testid={`download-${fmt}`} onClick={() => exportFile(fmt)} disabled={!!downloading}
                        className="btn-ghost rounded-full h-9 px-4 flex items-center gap-1.5 text-sm disabled:opacity-50 uppercase">
                        {downloading === fmt ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} {fmt}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mt-4 mb-3"><Share2 size={14} /> Share</div>
                  <div className="flex flex-wrap gap-2">
                    <button data-testid="share-email" onClick={emailShare} className="btn-ghost rounded-full h-9 px-4 flex items-center gap-1.5 text-sm"><Mail size={14} /> Email</button>
                    <button data-testid="share-whatsapp" onClick={() => shareTo("whatsapp")} className="btn-ghost rounded-full h-9 px-4 flex items-center gap-1.5 text-sm"><MessageCircle size={14} /> WhatsApp</button>
                    <button data-testid="share-facebook" onClick={() => shareTo("facebook")} className="btn-ghost rounded-full h-9 px-4 flex items-center gap-1.5 text-sm"><Share2 size={14} /> Facebook</button>
                    <button data-testid="share-x" onClick={() => shareTo("x")} className="btn-ghost rounded-full h-9 px-4 flex items-center gap-1.5 text-sm"><Share2 size={14} /> X</button>
                  </div>
                  <p className="text-[11px] text-sec mt-3">Every file carries a “Done with My Kind” footer linking back to My Kind.</p>
                </div>

                <button data-testid="new-blueprint" onClick={() => setResult(null)} className="btn-ghost rounded-full w-full h-11 flex items-center justify-center gap-2">
                  <X size={16} /> Architect another
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ icon: I, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-2"><I size={14} /> {title}</div>
      {children}
    </div>
  );
}

function useRotating(list, ms = 4500) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((x) => (x + 1) % list.length), ms);
    return () => clearInterval(id);
  }, [list, ms]);
  return list[i];
}

// A personalized, time-of-day & health-aware "grab a break" experience while the app builds.
function BuildBreak({ label }) {
  const { hour, drinks, emoji } = useMemo(() => {
    const h = new Date().getHours();
    const bucket = h < 5 ? "late" : h < 11 ? "morning" : h < 15 ? "midday" : h < 19 ? "afternoon" : h < 22 ? "evening" : "late";
    const byTime = {
      morning: ["warm water with lemon to wake up gently", "a fresh cup of green tea", "a seasonal fruit", "a light, wholesome breakfast"],
      midday: ["a proper lunch — you've earned it", "a bowl of cut fruit", "chilled buttermilk or coconut water", "a big glass of water + a 2-minute stretch"],
      afternoon: ["herbal or green tea (skip the extra coffee)", "a small handful of nuts", "a crisp apple", "a tall glass of water"],
      evening: ["a caffeine-free herbal tea", "a light, healthy snack", "a short walk to reset", "water — stay hydrated"],
      late: ["warm milk or chamomile — skip the caffeine now", "a glass of water and rest your eyes", "keep it light — maybe a fruit"],
    };
    const emojiByTime = { morning: "🍵", midday: "🥗", afternoon: "🍵", evening: "🌿", late: "🥛" };
    return { hour: h, drinks: byTime[bucket], emoji: emojiByTime[bucket] };
  }, []);
  const cleanLabel = label === "app" ? "app" : label;
  const openers = useMemo(() => [
    `Sit back — My Kind is crafting your ${cleanLabel}.`,
    `Your ${cleanLabel} is coming to life, pixel by pixel.`,
    `Hang tight — wiring up your ${cleanLabel} now.`,
    `Good things take a moment. Building your ${cleanLabel}…`,
  ], [cleanLabel]);
  const explore = useMemo(() => [
    { to: "/vault", label: "peek at My Vault" },
    { to: "/economy", label: "check your Earnings" },
    { to: "/businesses", label: "browse the Directory" },
    { to: "/personal", label: "open your Personal space" },
  ], []);
  const drink = useRotating(drinks);
  const opener = useRotating(openers, 5000);
  return (
    <div className="rounded-2xl border border-rud bg-elevated p-6 text-center" style={{ minHeight: 300 }} data-testid="app-building">
      <div className="relative mx-auto w-14 h-14 mb-4">
        <div className="absolute inset-0 rounded-full brand-glow" />
        <HeartLoader />
      </div>
      <p className="font-serif text-2xl tracking-tight" data-testid="build-opener">{opener}</p>
      <p className="text-sm text-sec mt-2">This usually takes ~30–60s. No need to wait around.</p>
      <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-brand/40 bg-brand/10 px-4 py-2 text-sm">
        <span className="text-lg">{emoji}</span>
        <span data-testid="build-break-suggestion">Why not grab {drink}?</span>
      </div>
      <p className="text-[11px] text-sec mt-2">Tuned to the time of day &amp; a healthier you — not always coffee.</p>
      <div className="mt-5 text-sm text-sec">
        Or keep exploring —{" "}
        {explore.map((e, i) => (
          <React.Fragment key={e.to}>
            <Link to={e.to} className="text-brand hover:underline">{e.label}</Link>
            {i < explore.length - 1 ? " · " : ""}
          </React.Fragment>
        ))}
        . We'll have it ready when you're back.
      </div>
    </div>
  );
}
